<template>
  <div :class="popoverClasses" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <slot />
    <div
      v-if="show && !props.hide"
      class="base-popover__content"
      @mouseenter="handleContentMouseEnter"
      @mouseleave="handleContentMouseLeave"
    >
      <slot name="popover" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'

  type Position = 'top' | 'bottom' | 'left' | 'right' | 'center'
  type Align = 'left' | 'right' | 'center'

  const props = defineProps({
    align: {
      default: 'center',
      type: String as () => Align,
    },
    hide: {
      default: false,
      type: Boolean,
    },
    position: {
      default: 'top',
      type: String as () => Position,
    },
  })

  const popoverClasses = computed(() => {
    const classes = ['base-popover']

    if (props.position === 'top' || props.position === 'bottom') {
      classes.push(`base-popover--${props.position}`)
      if (props.align !== 'center') {
        classes.push(`base-popover--${props.align}`)
      } else {
        classes.push('base-popover--center')
      }
    } else {
      classes.push(`base-popover--${props.position}`)
    }

    return classes.join(' ')
  })

  const show = ref(false)
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const handleMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    show.value = true
  }

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      show.value = false
    }, 100) // Small delay to allow moving to popover content
  }

  const handleContentMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    show.value = true
  }

  const handleContentMouseLeave = () => {
    show.value = false
  }
</script>

<style scoped>
  .base-popover {
    position: relative;
    display: block;
    width: max-content;
  }

  .base-popover__content {
    position: absolute;
    z-index: 8000;
    width: max-content;
    max-width: 300px;
    padding: 12px;
    font-family: Montserrat-Regular;
    font-size: 12px;
    line-height: 14px;
    pointer-events: auto; /* Allow interaction with content */
    background: #fff;
    border: 1px solid var(--color-main-100);
    border-radius: 4px;
  }

  .base-popover__content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .base-popover__title {
    font-family: Montserrat-SemiBold;
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
    color: #000;
  }

  .base-popover__text {
    font-family: Montserrat-Regular;
    font-size: 12px;
    line-height: 16px;
    color: #000;
  }

  .base-popover__link {
    font-family: Montserrat-Regular;
    font-size: 12px;
    line-height: 14px;
    color: var(--color-main-100);
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .base-popover__link:hover {
    color: var(--color-main-110);
  }

  .base-popover__content::before,
  .base-popover__content::after {
    position: absolute;
    z-index: 8001;
    width: 0;
    height: 0;
    pointer-events: none;
    content: "";
    border-right: 4px solid transparent;
    border-left: 4px solid transparent;
  }

  /* Top */
  .base-popover--top .base-popover__content {
    bottom: calc(100% + 9px);
  }

  .base-popover--top .base-popover__content::before {
    bottom: -9px;
    border-top: 8px solid var(--color-main-100);
  }

  .base-popover--top .base-popover__content::after {
    bottom: -8px;
    border-top: 8px solid #fff;
  }

  /* Bottom */
  .base-popover--bottom .base-popover__content {
    top: calc(100% + 9px);
  }

  .base-popover--bottom .base-popover__content::before {
    top: -9px;
    border-bottom: 8px solid var(--color-main-100);
  }

  .base-popover--bottom .base-popover__content::after {
    top: -8px;
    border-bottom: 8px solid #fff;
  }

  /* Left */
  .base-popover--left .base-popover__content {
    left: 50%;
    transform: translate(-14px, 0);
  }

  .base-popover--left .base-popover__content::before {
    left: 10px;
  }

  .base-popover--left .base-popover__content::after {
    left: 10px;
  }

  /* Center */
  .base-popover--center .base-popover__content {
    left: 50%;
    transform: translate(-50%, 0);
  }

  .base-popover--center .base-popover__content::before {
    left: calc(50% - 4px);
  }

  .base-popover--center .base-popover__content::after {
    left: calc(50% - 4px);
  }

  /* Right */
  .base-popover--right .base-popover__content {
    right: 50%;
    transform: translate(14px, 0);
  }

  .base-popover--right .base-popover__content::before {
    right: 10px;
  }

  .base-popover--right .base-popover__content::after {
    right: 10px;
  }
</style>
